import { useState, useEffect } from 'react'

// ── Device type (UA-based, stable for the session) ──────────────────────────
//
// Determines the physical device class from the user-agent string. This is
// computed once at module load and never changes — a laptop is always a laptop,
// even if the user resizes the window or attaches/detaches peripherals.
//
// iPadOS 13+ sends a desktop Safari UA ("Macintosh; Intel Mac OS X ...") with
// no "iPad" in the string. The only reliable way to distinguish it from a real
// Mac is `navigator.maxTouchPoints`: Macs report 0, iPads report 5+.

export type DeviceType = 'phone' | 'tablet' | 'desktop'

export const getDeviceType = (): DeviceType => {
  if (typeof navigator === 'undefined') return 'desktop'

  const ua = navigator.userAgent || ''

  // iPadOS 13+ masquerades as desktop Safari — catch it via maxTouchPoints.
  // Real Macs report 0, real iPads report 5, Chrome DevTools emulation reports 1.
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 0) return 'tablet'

  if (/iPhone|iPod/i.test(ua)) return 'phone'
  if (/iPad/i.test(ua)) return 'tablet' // older iPadOS

  // Android phones include "Mobile" in the UA, tablets don't
  if (/Android/i.test(ua)) return /Mobile/i.test(ua) ? 'phone' : 'tablet'

  // Windows, Linux, ChromeOS — desktop regardless of touchscreen
  return 'desktop'
}

/** Physical device class — computed once at module load, never changes. */
export const DEVICE_TYPE = getDeviceType()

/** True for phones and tablets, false for desktops (including touchscreen laptops). */
export const IS_MOBILE_DEVICE = DEVICE_TYPE !== 'desktop'

export interface BreakpointState {
  // Granular breakpoints (all width-based)
  isPhone: boolean    // width < 768 (matches MUI sm breakpoint)
  isTablet: boolean   // 768 <= width < 1025
  isDesktop: boolean  // width >= 1025

  // Backward-compatible (isPhone || isTablet)
  isMobile: boolean   // width < 1025

  // UA-based device classification — stable for the entire session.
  // Use this for decisions about *controls* (toolbar, crosshair, drawing mode)
  // rather than layout.
  deviceType: DeviceType
  isMobileDevice: boolean // deviceType !== 'desktop'

  // Orientation
  isLandscape: boolean // width > height
  isPortrait: boolean  // width <= height

  // Raw values
  width: number
  height: number
}

// Centralized breakpoint values - single source of truth
// Aligned with MUI theme breakpoints (sm: 768, md: 1024)
export const BREAKPOINTS = {
  phone: 768,   // < 768px is phone (matches MUI sm breakpoint)
  mobile: 1025, // <= 1024px is mobile/tablet (iPad landscape), > 1024px is desktop
} as const

const useBreakpoint = (): BreakpointState => {
  const [dimensions, setDimensions] = useState(() => {
    if (typeof window === 'undefined') {
      return { width: 0, height: 0 }
    }
    return {
      width: window.innerWidth,
      height: window.innerHeight,
    }
  })

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    // Set initial dimensions
    handleResize()

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const { width, height } = dimensions

  // Width-based checks first
  const isPhone = width < BREAKPOINTS.phone
  const isNarrow = width < BREAKPOINTS.mobile

  // isMobile is width-only: a wide-viewport device gets the desktop layout
  // even if its primary input is touch (Surface Pro detached, iPad Pro 13",
  // etc. have plenty of room for the full UI). For control decisions (crosshair,
  // toolbar, drawing mode) use `isMobileDevice` (UA-based) instead.
  const isMobile = isNarrow
  const isDesktop = !isMobile
  const isTablet = isMobile && !isPhone

  const isLandscape = width > height
  const isPortrait = width <= height

  return {
    isPhone,
    isTablet,
    isDesktop,
    isMobile,
    deviceType: DEVICE_TYPE,
    isMobileDevice: IS_MOBILE_DEVICE,
    isLandscape,
    isPortrait,
    width,
    height,
  }
}

export default useBreakpoint
